import { Alert, Button, Stack } from '@mui/material'
import { useContext } from 'react'
import AppContext from '../../../../context/appContext'
import { useCreateExclusionAreas } from '../../../../hooks/data/useExclusionAreas'
import { ExclusionAreaGeoJSON, RefData } from '../../../../types/apiModels'
import { useCustomSnackbarError } from '../../../../utils/Snackbar/useCustomSnackbarError'
import { MonitoringAccordion } from '../../MonitoringAccordion'
import { getFeatureId } from '../../utils'
import { useExclusionAreaContext } from '../ExclusionAreaContext'
import { useExclusionAreaWriteError } from '../useExclusionAreaWriteError'
import { ExclusionAreaEditItem } from './ExclusionAreaEditItem'
import { ExclusionAreaItem } from './ExclusionAreaItem'
import { validateExclusionAreasList } from '../validationUtils'

interface ImportedExclusionAreasAccordionProps {
  exclusionAreaTypes: RefData[] | undefined
  isLoading: boolean
  canEdit: boolean
  isInEditMode: boolean
  isDrawingMode: boolean
}

export const ImportedExclusionAreasAccordion = ({
  exclusionAreaTypes,
  isLoading,
  canEdit,
  isInEditMode,
  isDrawingMode,
}: ImportedExclusionAreasAccordionProps) => {
  const {
    importedExclusionAreas,
    setImportedExclusionAreas,
    editingItemId,
    selectedArea,
    startEditing,
    updateSelectedArea,
    cancelEdit,
    finishEdit,
    originalArea,
  } = useExclusionAreaContext()

  const { currentFarm, setPartialChanges, setConfirmationModalConfig } = useContext(AppContext)
  const { handleError } = useCustomSnackbarError()
  const { handleWriteError, blockedPeriodDialog } = useExclusionAreaWriteError()

  const createExclusionAreas = useCreateExclusionAreas({ farmId: currentFarm?.id })

  const handleEdit = (exclusionArea: ExclusionAreaGeoJSON) => {
    startEditing(getFeatureId(exclusionArea)!, exclusionArea)
  }

  const handleUpdateImportedArea = (updatedArea: ExclusionAreaGeoJSON) => {
    setImportedExclusionAreas(prev =>
      prev.map(area => (getFeatureId(area) === getFeatureId(updatedArea) ? updatedArea : area)),
    )
    finishEdit()
    setPartialChanges(true)
  }

  const handleSaveImported = async () => {
    const validationError = validateExclusionAreasList(importedExclusionAreas)

    if (validationError) {
      handleError(validationError)
      return
    }

    setConfirmationModalConfig({
      open: true,
      title: 'Guardar áreas importadas',
      message: '¿Está seguro de querer guardar estas áreas de exclusión?',
      confirmAction: async () => {
        try {
          const featuresToCreate = importedExclusionAreas.map(area => {
            const newArea = structuredClone(area)
            delete newArea.properties.tmpId
            delete newArea.properties.isImported
            return newArea
          })

          await createExclusionAreas.mutateAsync({
            features: featuresToCreate,
            exclusionTypes: exclusionAreaTypes || [],
          })

          setImportedExclusionAreas([])
          setPartialChanges(false)
        } catch (error) {
          handleWriteError(error)
        }
      },
    })
  }

  const handleCancelImport = () => {
    setImportedExclusionAreas([])
    setPartialChanges(false)
  }

  const handleDelete = (exclusionArea: ExclusionAreaGeoJSON) => {
    setImportedExclusionAreas(prev =>
      prev.filter(area => getFeatureId(area) !== getFeatureId(exclusionArea)),
    )
    setPartialChanges(false)
  }

  const handleCancelEditAction = () => {
    if (editingItemId && originalArea) {
      setImportedExclusionAreas(prev =>
        prev.map(area => (getFeatureId(area) === editingItemId ? originalArea : area)),
      )
    }
    cancelEdit()
    setPartialChanges(false)
  }

  return (
    <>
      <MonitoringAccordion
        defaultExpanded
        enableSelection
        getItemId={getFeatureId}
        isLoading={isLoading}
        items={importedExclusionAreas}
        onItemsChange={items => setImportedExclusionAreas(items as ExclusionAreaGeoJSON[])}
      >
        <MonitoringAccordion.Title enableSelection>Areas a importar</MonitoringAccordion.Title>
        <MonitoringAccordion.Description>
          Revise y clasifique las áreas importadas antes de confirmar.
        </MonitoringAccordion.Description>
        <MonitoringAccordion.Item>
          <Alert severity="warning" sx={{ width: '100%' }}>
            Se sobreescribirán las áreas de exclusión existentes, se agregarán las nuevas y estas
            recortarán a las existentes en caso de solaparse.
          </Alert>
        </MonitoringAccordion.Item>
        {importedExclusionAreas.map(exclusionArea => {
          const areaId = getFeatureId(exclusionArea)
          return (
            <MonitoringAccordion.Item key={areaId}>
              {editingItemId === areaId ? (
                <ExclusionAreaEditItem
                  exclusionArea={selectedArea || exclusionArea}
                  exclusionAreaTypes={exclusionAreaTypes}
                  onCancel={handleCancelEditAction}
                  onChange={updateSelectedArea}
                  onSave={handleUpdateImportedArea}
                />
              ) : (
                <>
                  <MonitoringAccordion.Item.Check itemId={areaId} />
                  <ExclusionAreaItem
                    exclusionArea={exclusionArea}
                    exclusionAreaTypes={exclusionAreaTypes}
                    isDisabled={isInEditMode || isDrawingMode || !canEdit}
                    onDelete={() => handleDelete(exclusionArea)}
                    onEdit={() => handleEdit(exclusionArea)}
                  />
                </>
              )}
            </MonitoringAccordion.Item>
          )
        })}
        <MonitoringAccordion.Item>
          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ p: 2, width: '100%' }}>
            <Button color="error" onClick={handleCancelImport}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSaveImported}>
              Confirmar
            </Button>
          </Stack>
        </MonitoringAccordion.Item>
      </MonitoringAccordion>
      {blockedPeriodDialog}
    </>
  )
}
